import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ImagePlus, Trash2, Check } from "lucide-react";
import { api, imageUrl } from "@/lib/api";

const INPUT = "w-full px-3 py-2 rounded-lg border border-[#D5CCBC] bg-white text-sm text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload?folder=gallery", fd);
  return data.url;
}

export default function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [eventImage, setEventImage] = useState("");
  const [heroImage, setHeroImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const eventRef = useRef(null);

  const load = () =>
    api
      .get("/gallery")
      .then((r) => {
        setItems(r.data.items);
        setEventImage(r.data.event_image_url);
        setHeroImage(r.data.hero_image_url || "");
      })
      .catch(() => toast.error("Gagal memuat galeri"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const pickNew = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("upload");
    try {
      setNewUrl(await uploadFile(f));
      toast.success("Foto terunggah, isi label lalu simpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah foto");
    } finally {
      setBusy("");
      e.target.value = "";
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newUrl) return toast.error("Unggah foto terlebih dahulu");
    setBusy("add");
    try {
      await api.post("/gallery", { image_url: newUrl, tag: newTag });
      toast.success("Foto ditambahkan ke galeri");
      setNewTag("");
      setNewUrl("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menambah foto");
    } finally {
      setBusy("");
    }
  };

  const saveTag = async (item) => {
    try {
      await api.put(`/gallery/${item.id}`, { image_url: item.image_url, tag: item.tag });
      toast.success("Label disimpan");
    } catch {
      toast.error("Gagal menyimpan label");
    }
  };

  const replaceImage = async (item, file) => {
    setBusy(item.id);
    try {
      const url = await uploadFile(file);
      await api.put(`/gallery/${item.id}`, { image_url: url, tag: item.tag });
      toast.success("Foto diganti");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengganti foto");
    } finally {
      setBusy("");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Hapus foto "${item.tag}" dari galeri?`)) return;
    try {
      await api.delete(`/gallery/${item.id}`);
      toast.success("Foto dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus foto");
    }
  };

  const move = async (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.put("/gallery/reorder", { ids: next.map((x) => x.id) }).catch(() => toast.error("Gagal mengubah urutan"));
  };

  const replaceEvent = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("event");
    try {
      const url = await uploadFile(f);
      await api.put("/gallery/event-image", { image_url: url });
      setEventImage(url);
      toast.success("Foto portofolio acara diganti");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengganti foto acara");
    } finally {
      setBusy("");
      e.target.value = "";
    }
  };

  const heroRef = useRef(null);

  const replaceHero = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("hero");
    try {
      const url = await uploadFile(f);
      await api.put("/gallery/hero-image", { image_url: url });
      setHeroImage(url);
      toast.success("Foto utama halaman depan diganti");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengganti foto utama");
    } finally {
      setBusy("");
      e.target.value = "";
    }
  };

  const resetHero = async () => {
    try {
      await api.delete("/gallery/hero-image");
      setHeroImage("");
      toast.success("Foto utama dikembalikan ke bawaan");
    } catch {
      toast.error("Gagal mengembalikan foto utama");
    }
  };

  if (loading) return <p data-testid="gallery-admin-loading" className="text-sm text-[#6B7C66]">Memuat galeri...</p>;

  return (
    <div data-testid="gallery-admin">
      <div className="mb-8">
        <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">Galeri & Foto Halaman</h1>
        <p className="text-xs text-[#6B7C66] mt-1">
          Kelola foto utama (hero), galeri "Karya Tangan Florist Kami", dan foto "Portofolio Acara". Format JPG/PNG/WEBP, maks 5 MB.
        </p>
      </div>

      <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5 mb-8 flex flex-col sm:flex-row gap-5 sm:items-center">
        <div className="w-full sm:w-72 aspect-[16/9] rounded-xl overflow-hidden bg-[#EFE9DD] shrink-0 relative">
          {heroImage ? (
            <img src={imageUrl(heroImage)} alt="Foto utama" data-testid="gallery-hero-preview" className="w-full h-full object-cover" />
          ) : (
            <div data-testid="gallery-hero-default" className="w-full h-full flex items-center justify-center text-[11px] text-[#7E8F7A] px-4 text-center">
              Menggunakan foto bawaan
            </div>
          )}
        </div>
        <div className="flex-1">
          <span className="text-sm font-bold text-[#1E2B1D] block">Foto Utama Halaman Depan (Hero)</span>
          <p className="text-xs text-[#6B7C66] mt-1 mb-3">Foto besar paling atas yang pertama dilihat pembeli. Disarankan foto landscape beresolusi tinggi.</p>
          <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={replaceHero} data-testid="gallery-hero-file-input" />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => heroRef.current?.click()}
              disabled={busy === "hero"}
              data-testid="gallery-hero-upload-btn"
              className="px-4 py-2 rounded-lg bg-[#3D5A40] hover:bg-[#2E4530] text-[#FAF7EE] text-xs font-semibold disabled:opacity-50 transition-colors"
            >
              {busy === "hero" ? "Mengunggah..." : "Ganti Foto Utama"}
            </button>
            {heroImage && (
              <button type="button" onClick={resetHero} data-testid="gallery-hero-reset-btn" className="px-4 py-2 rounded-lg border border-[#D5CCBC] text-[#5E6D5B] text-xs font-semibold hover:bg-[#F4EFE6] transition-colors">
                Kembalikan Bawaan
              </button>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={addItem} className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5 mb-8 flex flex-col sm:flex-row gap-4 sm:items-end">
        <div className="w-24 h-32 rounded-xl bg-[#EFE9DD] border border-dashed border-[#C5BCAB] overflow-hidden flex items-center justify-center shrink-0">
          {newUrl ? (
            <img src={imageUrl(newUrl)} alt="Pratinjau" className="w-full h-full object-cover" data-testid="gallery-new-preview" />
          ) : (
            <ImagePlus className="w-6 h-6 text-[#A39B8A]" />
          )}
        </div>
        <div className="flex-1 space-y-3">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickNew} data-testid="gallery-new-file-input" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy === "upload"}
            data-testid="gallery-new-upload-btn"
            className="px-4 py-2 rounded-lg border border-[#3D5A40] text-[#3D5A40] text-xs font-semibold hover:bg-[#EAF2E9] disabled:opacity-50 transition-colors"
          >
            {busy === "upload" ? "Mengunggah..." : newUrl ? "Ganti Foto" : "Pilih Foto"}
          </button>
          <input
            required
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Label foto, mis. Buket Wisuda"
            data-testid="gallery-new-tag-input"
            className={INPUT}
          />
        </div>
        <button
          type="submit"
          disabled={busy === "add"}
          data-testid="gallery-add-btn"
          className="px-5 py-2.5 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-50 text-[#FAF7EE] text-sm font-bold transition-colors"
        >
          {busy === "add" ? "Menyimpan..." : "Tambah ke Galeri"}
        </button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {items.map((item, i) => (
          <div key={item.id} data-testid={`gallery-admin-item-${item.id}`} className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] overflow-hidden">
            <div className="aspect-[3/4] relative bg-[#EFE9DD]">
              <img src={imageUrl(item.image_url)} alt={item.tag} className="absolute inset-0 w-full h-full object-cover" />
              <span className="absolute top-2 left-2 text-[10px] font-bold bg-[#1E2B1D]/70 text-white px-2 py-0.5 rounded-full">#{i + 1}</span>
              <label className="absolute bottom-2 right-2 text-[10px] font-semibold bg-[#FBF9F5]/95 text-[#1E2B1D] px-2 py-1 rounded-md cursor-pointer hover:bg-white">
                {busy === item.id ? "Mengunggah..." : "Ganti foto"}
                <input type="file" accept="image/*" className="hidden" data-testid={`gallery-replace-input-${item.id}`} onChange={(e) => e.target.files?.[0] && replaceImage(item, e.target.files[0])} />
              </label>
            </div>
            <div className="p-3 space-y-2">
              <div className="flex gap-1.5">
                <input
                  value={item.tag}
                  onChange={(e) => setItems((list) => list.map((x) => (x.id === item.id ? { ...x, tag: e.target.value } : x)))}
                  data-testid={`gallery-tag-input-${item.id}`}
                  className={`${INPUT} text-xs`}
                />
                <button onClick={() => saveTag(item)} aria-label="Simpan label" data-testid={`gallery-tag-save-${item.id}`} className="p-2 rounded-lg bg-[#3D5A40] text-white hover:bg-[#2E4530]">
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Geser ke kiri" data-testid={`gallery-move-up-${item.id}`} className="p-1.5 rounded-lg border border-[#D5CCBC] text-[#3D5A40] disabled:opacity-30 hover:bg-[#F4EFE6]">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Geser ke kanan" data-testid={`gallery-move-down-${item.id}`} className="p-1.5 rounded-lg border border-[#D5CCBC] text-[#3D5A40] disabled:opacity-30 hover:bg-[#F4EFE6]">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button onClick={() => remove(item)} aria-label="Hapus foto" data-testid={`gallery-delete-${item.id}`} className="p-1.5 rounded-lg border border-[#D5CCBC] text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5 flex flex-col sm:flex-row gap-5 sm:items-center">
        <div className="w-full sm:w-64 aspect-[16/10] rounded-xl overflow-hidden bg-[#EFE9DD] shrink-0">
          {eventImage && <img src={imageUrl(eventImage)} alt="Portofolio acara" data-testid="gallery-event-preview" className="w-full h-full object-cover object-[center_60%]" />}
        </div>
        <div className="flex-1">
          <span className="text-sm font-bold text-[#1E2B1D] block">Foto Portofolio Acara</span>
          <p className="text-xs text-[#6B7C66] mt-1 mb-3">Foto besar di bagian "Dekorasi Acara & Pernikahan". Disarankan foto landscape.</p>
          <input ref={eventRef} type="file" accept="image/*" className="hidden" onChange={replaceEvent} data-testid="gallery-event-file-input" />
          <button
            type="button"
            onClick={() => eventRef.current?.click()}
            disabled={busy === "event"}
            data-testid="gallery-event-upload-btn"
            className="px-4 py-2 rounded-lg border border-[#3D5A40] text-[#3D5A40] text-xs font-semibold hover:bg-[#EAF2E9] disabled:opacity-50 transition-colors"
          >
            {busy === "event" ? "Mengunggah..." : "Ganti Foto Acara"}
          </button>
        </div>
      </div>
    </div>
  );
}
